import React, { ReactElement, useCallback, useMemo } from 'react';
import accounting from 'accounting';
import { createUseStyles } from 'react-jss';
import { type DocumentNode, type ApolloError, type InternalRefetchQueriesInclude } from '@apollo/client';
import { Theme, MiniTooltip, Button, MultiButton, UnawardIcon, AlertService, icons, AwardIcon, NegotiateIcon } from '@spoiler-alert/ui-library';
import { footerStyles } from './offer-card-styles';
import { Field } from '../helpers';
import { NegotiationStrings } from '../../../string-resources';
import { negotiationOrigin } from '../../../enums';
import QuickEntryWithTextArea from '../quick-entry';
import Tracking from '../../../utilities/tracking-apis';
import type { BackendError, MultiButtonType, OfferCardUser } from './offer-card-types';
import { Inventory, Listing, OfferListing } from '../../../graphql/query-result-types';
import { POSSIBLE_ACTIONS } from './offer-card-enums';

const useStyles = createUseStyles(footerStyles, { name: 'OfferCardFooter' });

export interface ActionTooltipOverride {
  text: React.ReactNode;
  textWidth?: number;
}

interface OfferCardFooterProps {
  offerInfo: OfferListing;
  inventory: Inventory;
  buyerSite: {
    _id: string;
    siteName: string;
  };
  listing: Listing;
  ignoredClass: string;
  quantity: number;
  pricePerCase: number;
  isAwarded: boolean;
  isDonation: boolean;
  enabledActions: POSSIBLE_ACTIONS[];
  handleUnawardAndNegotiate: () => void;
  checkAwardQuantity: () => void;
  isIgnored: boolean;
  actionInProgress: boolean;
  ignoreInProgress: boolean;
  negotiationInProgress: boolean;
  handleRestore: () => void;
  handleIgnore: () => void;
  handleUnaward: () => void;
  canAward: () => boolean;
  disabled: boolean;
  refetchQueries: (isNegotiation: boolean) => InternalRefetchQueriesInclude;
  UnawardAndNegotiateOfferListing: DocumentNode;
  handleNegotiation: () => void;
  createNegotiationStagedListings: DocumentNode;
  user: OfferCardUser;
  handleAcceptPostAwardUpdateBid: () => void;
  handleRejectPostAwardUpdateBid: () => void;
  actionTooltipOverrides: {
    [POSSIBLE_ACTIONS.AWARD]?: ActionTooltipOverride;
    [POSSIBLE_ACTIONS.IGNORE]?: ActionTooltipOverride;
    [POSSIBLE_ACTIONS.NEGOTIATE]?: ActionTooltipOverride;
  };
}

enum NegotiationMutations {
  UnawardAndNegotiateOfferListing = 'unawardAndNegotiateOfferListing',
  CreateNegotiationStagedListings = 'createNegotiationStagedListings',
}

interface NegotiationMutationResponseResult {
  errors: BackendError[];
  negotiationListings?: {
    suggestedQuantity: number;
    suggestedUnitPrice: number;
  }[];
}

interface NegotiationMutationResponse {
  [NegotiationMutations.CreateNegotiationStagedListings]: NegotiationMutationResponseResult;
  [NegotiationMutations.UnawardAndNegotiateOfferListing]: NegotiationMutationResponseResult;
}

const OfferCardFooter: React.FC<OfferCardFooterProps> = ({
  ignoredClass,
  offerInfo,
  inventory,
  buyerSite,
  listing,
  quantity,
  pricePerCase,
  isAwarded,
  isDonation,
  handleUnawardAndNegotiate,
  checkAwardQuantity,
  isIgnored,
  actionInProgress,
  ignoreInProgress,
  negotiationInProgress,
  handleRestore,
  handleIgnore,
  handleUnaward,
  canAward,
  enabledActions,
  disabled,
  refetchQueries,
  UnawardAndNegotiateOfferListing,
  handleNegotiation,
  createNegotiationStagedListings,
  user,
  handleAcceptPostAwardUpdateBid,
  handleRejectPostAwardUpdateBid,
  actionTooltipOverrides,
}) => {
  const classes = useStyles();
  const totalPrice = quantity * pricePerCase * (offerInfo.pricingDivisor || 1);
  const totalUpdatedPrice = offerInfo.postAwardUpdates?.buyerUpdatedTotalPrice || 0;
  const onQuickEntryError = (err: BackendError) => {
    const message = err && err.code === 'NEGOTIATION_PUBLISHED' ? err.message : NegotiationStrings.genericError;
    AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{message}</span>, dismissDelay: 5000 });
  };
  const onServerError = (err: ApolloError) => {
    AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{err.message}</span>, dismissDelay: 5000 });
  };

  const onQuickEntryResponse = useCallback(
    (result: NegotiationMutationResponse, mutationName: NegotiationMutations) => {
      if (result?.[mutationName]?.errors?.length > 0) {
        onQuickEntryError(result[mutationName].errors[0]);
      } else {
        const negotiationListing = result?.[mutationName]?.negotiationListings?.[0];
        if (negotiationListing) {
          const negotiationQuantityChanged = quantity !== negotiationListing.suggestedQuantity;
          const negotiationPriceChanged = pricePerCase * (offerInfo.pricingDivisor || 1) !== negotiationListing.suggestedUnitPrice;
          if (!negotiationQuantityChanged && !negotiationPriceChanged) {
            Tracking.Event('OC - Negotiation Staged Secondary - No Adjustment', {
              userId: user._id,
              offerId: offerInfo._id,
            });
          } else if (negotiationQuantityChanged && negotiationPriceChanged)
            Tracking.Event('OC - Negotiation Staged - Price & Qty Adjusted', {
              userId: user._id,
              offerId: offerInfo._id,
            });
          else if (negotiationQuantityChanged)
            Tracking.Event('OC - Negotiation Staged - Qty Adjusted', {
              userId: user._id,
              offerId: offerInfo._id,
            });
          else if (negotiationPriceChanged)
            Tracking.Event('OC - Negotiation Staged - Price Adjusted', {
              userId: user._id,
              offerId: offerInfo._id,
            });
        }
        const messageInfo = `Negotiation started with ${buyerSite.siteName}`;
        AlertService.alert({ type: 'success', message: <span>{messageInfo}</span>, autoDismiss: true, dismissDelay: 5000 });
      }
    },
    [buyerSite.siteName, offerInfo._id, pricePerCase, quantity, user._id],
  );

  const onUnawardQuickEntryResponse = useCallback(
    (response: NegotiationMutationResponse) => {
      onQuickEntryResponse(response, NegotiationMutations.UnawardAndNegotiateOfferListing);
    },
    [onQuickEntryResponse],
  );

  const onNegotiateQuickEntryResponse = useCallback(
    (response: NegotiationMutationResponse) => {
      onQuickEntryResponse(response, NegotiationMutations.CreateNegotiationStagedListings);
    },
    [onQuickEntryResponse],
  );

  const inputsDisabled = useMemo(() => {
    return (
      disabled ||
      actionInProgress ||
      ignoreInProgress ||
      negotiationInProgress ||
      isAwarded ||
      isIgnored ||
      quantity > inventory.availableQuantity ||
      inventory.availableQuantity <= 0
    );
  }, [actionInProgress, disabled, ignoreInProgress, inventory.availableQuantity, isAwarded, isIgnored, negotiationInProgress, quantity]);

  const renderNegotiateButton = useCallback(
    (awarded: boolean) => {
      const negotiationPublished = offerInfo?.negotiation?.status === 'PUBLISHED' || offerInfo?.negotiation?.status === 'RECEIVED';
      let secondaryTooltipText: ReactElement | string = awarded ? 'Unaward & Adjust Counter Offer' : 'Adjust Counter Offer';
      let primaryAction: { onClick: () => void; tooltipText: ReactElement | string };
      let quickEntryArgs;
      let mutationArgs;

      if (awarded) {
        primaryAction = { onClick: handleUnawardAndNegotiate, tooltipText: 'Unaward & Negotiate' };

        quickEntryArgs = [
          { name: 'counterOfferQuantity', value: quantity, labelText: 'COUNTER QTY', max: listing.originalQuantity },
          { name: 'counterOfferPrice', value: pricePerCase, labelText: 'COUNTER PRICE', type: 'money' },
        ];

        mutationArgs = { id: offerInfo._id, negotiationOrigin: negotiationOrigin.OFFER_CARD_SECONDARY, counterOfferAnchor: 'Manual' };
      } else {
        primaryAction = { onClick: handleNegotiation, tooltipText: 'Negotiate' };

        quickEntryArgs = [
          { name: 'counterOffers.0.counterOfferQuantity', value: quantity, labelText: 'COUNTER QTY', max: listing.originalQuantity },
          { name: 'counterOffers.0.counterOfferPrice', value: pricePerCase, labelText: 'COUNTER PRICE', type: 'money' },
        ];

        mutationArgs = {
          counterOffers: [
            {
              offerListingId: offerInfo._id,
              counterOfferQuantity: quantity,
              counterOfferPrice: pricePerCase,
              counterOfferAnchor: 'Manual',
              reason: offerInfo?.negotiation?.reason || '',
            },
          ],
          negotiationOrigin: negotiationOrigin.OFFER_CARD_SECONDARY,
        };
      }

      if (negotiationPublished) {
        const text = (
          <>
            This offer already has
            <br /> a published negotiation.
          </>
        );
        primaryAction.tooltipText = text;
        secondaryTooltipText = text;
      }
      const alwaysDisabled =
        isIgnored ||
        actionInProgress ||
        ignoreInProgress ||
        negotiationInProgress ||
        negotiationPublished ||
        !enabledActions.includes(POSSIBLE_ACTIONS.NEGOTIATE);
      const unawardedDisabled = quantity > inventory.availableQuantity || inventory.availableQuantity <= 0;
      const TypedMultiButton = MultiButton as MultiButtonType;
      return (
        <>
          <TypedMultiButton
            primaryAction={primaryAction}
            secondaryTooltipText={secondaryTooltipText}
            disabled={awarded ? alwaysDisabled : unawardedDisabled || alwaysDisabled || !enabledActions.includes(POSSIBLE_ACTIONS.NEGOTIATE)}
            loading={negotiationInProgress}
            secondary
            primaryIcon={NegotiateIcon}
            child={
              <QuickEntryWithTextArea<NegotiationMutationResponse>
                mutation={awarded ? UnawardAndNegotiateOfferListing : createNegotiationStagedListings}
                mutationArgs={mutationArgs}
                quickEntryArgs={quickEntryArgs}
                onResponse={awarded ? onUnawardQuickEntryResponse : onNegotiateQuickEntryResponse}
                onError={onServerError}
                className={awarded ? `${classes.quickEntryWithTextArea} awarded` : classes.quickEntryWithTextArea}
                icon={NegotiateIcon as React.FC<{ style?: React.CSSProperties; className?: string }>}
                refetchQueries={refetchQueries(true)}
                quantity={quantity}
                originalQuantity={listing.originalQuantity}
                unitPrice={pricePerCase}
                pricingUOM={offerInfo.pricingUOM}
                pricingDivisor={offerInfo.pricingDivisor}
                reason={offerInfo?.negotiation?.reason || ''}
              />
            }
          />
        </>
      );
    },
    [
      UnawardAndNegotiateOfferListing,
      actionInProgress,
      classes.quickEntryWithTextArea,
      createNegotiationStagedListings,
      enabledActions,
      handleNegotiation,
      handleUnawardAndNegotiate,
      ignoreInProgress,
      inventory.availableQuantity,
      isIgnored,
      listing.originalQuantity,
      negotiationInProgress,
      offerInfo._id,
      offerInfo?.negotiation?.reason,
      offerInfo?.negotiation?.status,
      onNegotiateQuickEntryResponse,
      onUnawardQuickEntryResponse,
      pricePerCase,
      quantity,
      refetchQueries,
    ],
  );

  const restoreButton = useMemo(() => {
    const tooltipArgs = actionTooltipOverrides[POSSIBLE_ACTIONS.IGNORE] ? actionTooltipOverrides[POSSIBLE_ACTIONS.IGNORE] : { text: 'Restore' };
    return (
      <MiniTooltip {...tooltipArgs}>
        <Button
          className={classes.ignoreRestore__button}
          onClick={handleRestore}
          disabled={
            (inputsDisabled && !isIgnored) ||
            ignoreInProgress ||
            actionInProgress ||
            negotiationInProgress ||
            !enabledActions.includes(POSSIBLE_ACTIONS.IGNORE)
          }
          loading={ignoreInProgress}
          icon={icons.restore as React.FC<{ style?: React.CSSProperties; className?: string }>}
          secondary
        />
      </MiniTooltip>
    );
  }, [
    actionInProgress,
    actionTooltipOverrides,
    classes.ignoreRestore__button,
    enabledActions,
    handleRestore,
    ignoreInProgress,
    inputsDisabled,
    negotiationInProgress,
    isIgnored,
  ]);

  const ignoreTooltipArgs = useMemo(() => {
    if (!isAwarded && offerInfo?.negotiation?.status === 'STAGED') return { text: 'Unstage & Ignore' };
    if (actionTooltipOverrides[POSSIBLE_ACTIONS.IGNORE]) return actionTooltipOverrides[POSSIBLE_ACTIONS.IGNORE];
    return { text: 'Ignore' };
  }, [actionTooltipOverrides, isAwarded, offerInfo?.negotiation?.status]);

  const ignoreButton = useMemo(
    () => (
      <MiniTooltip {...ignoreTooltipArgs}>
        <Button
          className={classes.ignoreRestore__button}
          onClick={handleIgnore}
          disabled={
            inputsDisabled ||
            ignoreInProgress ||
            actionInProgress ||
            negotiationInProgress ||
            isAwarded ||
            !enabledActions.includes(POSSIBLE_ACTIONS.IGNORE)
          }
          loading={ignoreInProgress}
          icon={icons.ignore as React.FC<{ style?: React.CSSProperties; className?: string }>}
          secondary
        />
      </MiniTooltip>
    ),
    [
      actionInProgress,
      classes.ignoreRestore__button,
      enabledActions,
      handleIgnore,
      ignoreInProgress,
      ignoreTooltipArgs,
      inputsDisabled,
      isAwarded,
      negotiationInProgress,
    ],
  );

  const awardedButtons = useMemo(() => {
    const tooltipArgs = actionTooltipOverrides[POSSIBLE_ACTIONS.AWARD] ? actionTooltipOverrides[POSSIBLE_ACTIONS.AWARD] : { text: 'Unaward' };
    return (
      <>
        {offerInfo.status !== 'IGNORED' ? ignoreButton : restoreButton}
        {isDonation ? false : renderNegotiateButton(true)}
        <MiniTooltip {...tooltipArgs}>
          <Button
            warning
            onClick={handleUnaward}
            className={classes.unawardButton}
            icon={UnawardIcon as React.FC<{ style?: React.CSSProperties; className?: string }>}
            loading={actionInProgress}
            disabled={actionInProgress || ignoreInProgress || negotiationInProgress || !enabledActions.includes(POSSIBLE_ACTIONS.AWARD)}
          />
        </MiniTooltip>
      </>
    );
  }, [
    actionInProgress,
    actionTooltipOverrides,
    classes.unawardButton,
    enabledActions,
    handleUnaward,
    ignoreButton,
    ignoreInProgress,
    isDonation,
    negotiationInProgress,
    offerInfo.status,
    renderNegotiateButton,
    restoreButton,
  ]);

  const unawardedButtons = useMemo(() => {
    const tooltipArgs = actionTooltipOverrides[POSSIBLE_ACTIONS.AWARD] ? actionTooltipOverrides[POSSIBLE_ACTIONS.AWARD] : { text: 'Award' };
    return (
      <>
        {offerInfo.status !== 'IGNORED' ? ignoreButton : restoreButton}
        {isDonation ? false : renderNegotiateButton(false)}
        <MiniTooltip {...tooltipArgs} disabled={inputsDisabled}>
          <Button
            onClick={checkAwardQuantity}
            disabled={inputsDisabled || !canAward() || !enabledActions.includes(POSSIBLE_ACTIONS.AWARD)}
            loading={actionInProgress}
            icon={AwardIcon as React.FC<{ style?: React.CSSProperties; className?: string }>}
            className={classes.unawardButton}
          />
        </MiniTooltip>
      </>
    );
  }, [
    actionInProgress,
    actionTooltipOverrides,
    canAward,
    checkAwardQuantity,
    classes.unawardButton,
    enabledActions,
    ignoreButton,
    inputsDisabled,
    isDonation,
    offerInfo.status,
    renderNegotiateButton,
    restoreButton,
  ]);

  const acceptUpdatedBidButton = useMemo(
    () => (
      <>
        <MiniTooltip text="Reject Update">
          <Button
            icon={icons.circlereject as React.FC<{ style?: React.CSSProperties; className?: string }>}
            onClick={handleRejectPostAwardUpdateBid}
            warning
          />
        </MiniTooltip>
        <MiniTooltip text="Approve Update">
          <Button
            icon={icons.circlechecknew as React.FC<{ style?: React.CSSProperties; className?: string }>}
            className={`${classes.acceptedBidMargin}`}
            onClick={handleAcceptPostAwardUpdateBid}
          />
        </MiniTooltip>
      </>
    ),
    [classes, handleAcceptPostAwardUpdateBid, handleRejectPostAwardUpdateBid],
  );

  return (
    <div className={`${classes.offerCardFooter} ${ignoredClass}`}>
      {offerInfo.postAwardUpdates?.status == 'PENDING' ? (
        <div>
          <Field className={`${classes.offerCardTotalFieldInline} ${ignoredClass}`} label="Original Offer" color={Theme.greyDark}>
            <br />
            <span className={classes.offerCardTotal}>{accounting.formatMoney(totalPrice)}</span>
          </Field>
          <Field className={`${classes.offerCardTotalFieldInline} ${ignoredClass}`} label="Updated Offer" color={Theme.greyDark}>
            <br />
            <span className={classes.offerCardTotal}>{accounting.formatMoney(totalUpdatedPrice)}</span>
          </Field>
        </div>
      ) : (
        <Field className={`${classes.offerCardTotalFieldInline} ${ignoredClass}`} label="Total Offer" color={Theme.greyDark}>
          <br />
          <span className={classes.offerCardTotal}>{accounting.formatMoney(totalPrice)}</span>
        </Field>
      )}
      <div className={classes.offerCardButtons}>
        {offerInfo.postAwardUpdates?.status == 'PENDING' ? acceptUpdatedBidButton : isAwarded ? awardedButtons : unawardedButtons}
      </div>
    </div>
  );
};

export default OfferCardFooter;
